import React, { useMemo, useState } from 'react'
import { PageHeader, CommonFilters, CommonSelector, CommonCascador, TableDownload, UserAvatar } from '@components'
import { Card, Cascader, Table, TableProps, Popover, Button, Popconfirm, message, Descriptions, Select, Tag } from 'antd'
import { areaTree } from '@configs/area'
import { validPrimaryIndustryIncome, industryList } from 'src/components/CommonSelector/configs'
import { industryProducts } from 'src/components/CommonCascador/configs'
import { formatTime, trimStr, getArea } from '@library'
import { useTableService } from '@hooks/useTableService'
import { getLeadTransferList, exportLeadTransferList, transferSDRIntentionLead, rejectSDRIntentionLead } from '@services/lead'
import { needTotalEnum, LeadTransferItem, LeadTransferListFilter } from '@types'
import { ConfigTableHeader } from 'src/components/ConfigTableHeader'
import { TableIdEnum, getTableXwidth, getSortOrder } from '@configs/table'
import { useTableColumn } from '@hooks'
import { antdSortMap, customerTypeMap } from '@configs/map'
import { Link } from 'react-router-dom'
import store from '@store'
import { get, clone } from 'lodash'
import { FieldItem } from 'src/components/CommonFilters/configs'
import IntentionLeadModal from '@pages/customer/detail/components/IntentionLeadModal'
import TransferLeadAddModal from './components/TransferLeadAddModal'

const LeadSourceKeyMap = {
  SelfCreated: 0,
  LeadPlatformCreated: 1,
}

const LeadSourceMap = {
  [LeadSourceKeyMap.SelfCreated]: '企微孵化',
  [LeadSourceKeyMap.LeadPlatformCreated]: '线索平台',
}

const TransferStatusKeyMap = {
  ReadyToTransfer: 0,
  Transfering: 1,
  TransferSuccess: 2,
  TransferFailed: 3,
  Rejected: 4,
}

const TransferStatusMap = {
  [TransferStatusKeyMap.ReadyToTransfer]: '待流转',
  [TransferStatusKeyMap.Transfering]: '流转中',
  [TransferStatusKeyMap.TransferSuccess]: '流转成功',
  [TransferStatusKeyMap.TransferFailed]: '流转失败',
  [TransferStatusKeyMap.Rejected]: '已打回',
}

const TransferStatusColorMap = {
  [TransferStatusKeyMap.ReadyToTransfer]: 'default',
  [TransferStatusKeyMap.Transfering]: 'processing',
  [TransferStatusKeyMap.TransferSuccess]: 'success',
  [TransferStatusKeyMap.TransferFailed]: 'error',
  [TransferStatusKeyMap.Rejected]: 'warning',
}

export default function LeadListTransferPage () {
  const { authPointMap } = store.useGlobal()
  // 意向线索弹框
  const [editModalOpen, setEditModalOpen] = useState(false)
  // 上传企微孵化线索弹框
  const [transferEditModalOpen, setTransferEditModalOpen] = useState(false)
  const [transferLeadId, setTransferLeadId] = useState(null)
  const [editingLeadDetail, setEditingLeadDetail] = useState<{
    leadId?: number
    customerId?: number
    cid?: string
  }>()

  const initFilter = useMemo<any>(() => {
    return {
      page: 1,
      pageSize: 10,
      needTotal: needTotalEnum.ALL,
    }
  }, [])
  const {
    loading,
    list,
    reload,
    pagination,
    filter,
    setFilter,
    resetFilter,
    indexList,
    rowSelection,
    selectedRowText,
    rowKey,
  } = useTableService(getLeadTransferList, initFilter, '获取线索列表失败')
  const handleBatchTransfer = async () => {
    const ids = selectedLeadTransferList.filter(item => item.status === 0).map(item => item.id)
    if (!ids.length) {
      message.error('无可流转的线索')
      return
    }
    const [, err] = await transferSDRIntentionLead({ leadList: ids })
    if (err) {
      message.error(`流转失败(${err.message})`)
      return
    }
    message.success('意向线索正在流转中，稍后刷新列表查看结果')
    reload()
  }

  const handleSingleTransfer = async (record: LeadTransferItem) => {
    const [, err] = await transferSDRIntentionLead({ leadList: [record.id] })
    if (err) {
      message.error(`流转失败(${err.message})`)
      return
    }
    message.success('意向线索正在流转中，稍后刷新列表查看结果')
    reload()
  }

  const handleSingleReject = async (record: LeadTransferItem) => {
    const [, err] = await rejectSDRIntentionLead({ intentionLeadId: [record.id] })
    if (err) {
      message.error(`打回失败(${err.message})`)
      return
    }
    message.success('打回成功')
    reload()
  }

  const handleEdit = (record: LeadTransferItem) => {
    if (record.leadSource === LeadSourceKeyMap.SelfCreated) {
      setTransferEditModalOpen(true)
      setTransferLeadId(record.id)
    } else {
      setEditingLeadDetail({
        leadId: record.id,
        customerId: record.customerId,
        cid: record.cid,
      })
      setEditModalOpen(true)
    }
  }

  const handleEditModalOk = () => {
    setEditModalOpen(false)
    setEditingLeadDetail(undefined)
    reload()
  }

  const handleEditModalCancel = () => {
    setEditModalOpen(false)
    setEditingLeadDetail(undefined)
  }

  const handleTransferEditModalOk = () => {
    setTransferEditModalOpen(false)
    setTransferLeadId(null)
    reload()
  }

  const handleTransferEditModalCancel = () => {
    setTransferEditModalOpen(false)
    setTransferLeadId(null)
  }

  const selectedLeadTransferList = useMemo<LeadTransferItem[]>(() => indexList?.map?.(index => get(list, index)) || [], [list, indexList])
  const leadTransferList = useMemo<any[]>(() => list?.map(item => {
    const leadTransferData: any = clone(item)
    leadTransferData.area = getArea(leadTransferData.city, leadTransferData.province, '-')
    leadTransferData.needProduct = `${leadTransferData.needProductName1}-${leadTransferData.needProductName2}`
    leadTransferData.leadSourceName = LeadSourceMap[leadTransferData.leadSource]
    leadTransferData.detail = [
      { keyName: 'CID', keyValue: leadTransferData.cid },
      { keyName: '客户名称', keyValue: leadTransferData.customerName },
      { keyName: '销售通路', keyValue: leadTransferData.salesChannelName },
      { keyName: 'name商务', keyValue: leadTransferData.nameSales },
      { keyName: '客户类型', keyValue: customerTypeMap[leadTransferData.customerType] },
      { keyName: '客户所在区域', keyValue: leadTransferData.district },
      { keyName: '客户所在省市', keyValue: leadTransferData.area },
      { keyName: '客户所属行业', keyValue: leadTransferData.firstIncomeIndustryName || leadTransferData.industry },
      { keyName: '客户意向', keyValue: `${leadTransferData.intentionLevelName}-${leadTransferData.intentionSecondLevelName}` },
      { keyName: '关注产品', keyValue: leadTransferData.needProduct },
      { keyName: '意向采购时间', keyValue: leadTransferData.expectBuyDate },
      { keyName: '注册资本', keyValue: `人民币${leadTransferData.registeredCapital}万元` },
      { keyName: '公司规模', keyValue: leadTransferData.memberScale },
      { keyName: 'IT资金规模', keyValue: leadTransferData.monthConsumption },
      { keyName: '联系人姓名', keyValue: leadTransferData.contactName },
      { keyName: '联系人职务', keyValue: leadTransferData.contactPosition },
      { keyName: '联系人电话', keyValue: leadTransferData.contactPhone },
      { keyName: '联系人身份', keyValue: leadTransferData.position },
      { keyName: '客户需求', keyValue: leadTransferData.businessDemand },
      { keyName: '云厂商使用情况', keyValue: Array.isArray(leadTransferData.cloudVendorUse) ? leadTransferData.cloudVendorUse.join('、') : leadTransferData.cloudVendorUse },
    ]
    return leadTransferData
  }), [list])
  const transferFilterItems: FieldItem[] = [
    { fieldName: 'leadId', label: '原线索ID(如有)', type: 'input' },
    { fieldName: 'leadSource', label: '线索来源', render: () => <Select allowClear options={Object.keys(LeadSourceMap).map((key) => ({ label: LeadSourceMap[key], value: Number(key) })) || []} /> },
    { fieldName: 'origCustomerName', label: '原客户名称', type: 'customerNameSelect' },
    {
      fieldName: 'area',
      label: '客户所在省市',
      render: () => <Cascader options={areaTree} />,
      handleValue: value => {
        const [province, city] = value || [undefined, undefined]
        return { province, city }
      },
    },
    { fieldName: 'firstIncomeIndustryId', label: '客户所属行业', render: () => <CommonSelector {...industryList} valueKey="nodeCode" /> },
    { fieldName: 'industry', label: '客户所属行业-旧', render: () => <CommonSelector {...validPrimaryIndustryIncome} valueKey="primaryIndustryIncomeName" /> },
    { fieldName: 'registeredCapital', label: '注册资本（人民币万元）', type: 'inputNumber', componentProps: { min: 0, max: 99999999999999 } },
    {
      fieldName: 'needProduct',
      label: '关注产品',
      render: () => <CommonCascador {...industryProducts} changeOnSelect />,
      handleValue: value => {
        const [needProduct1, needProduct2] = value || [undefined, undefined]
        return { needProduct1, needProduct2 }
      },
    },
    { fieldName: 'salesChannel', label: '销售通路', type: 'cidSalesChannelSelect' },
    {
      fieldName: 'createTime',
      label: '提交时间',
      type: 'rangePicker',
      handleValue: val => ({
        createTimeFrom: formatTime(val?.[0], 'start'),
        createTimeTo: formatTime(val?.[1], 'end'),
      }),
    },
    {
      fieldName: 'batchId',
      label: '批次id',
      type: 'batchId',
    },
    {
      fieldName: 'follower',
      label: '跟进人',
      type: 'staffSelectMulti',
    },
    {
      fieldName: 'transferSales',
      label: '转出人',
      type: 'staffSelectMulti',
    },
    {
      fieldName: 'statusList',
      label: '流转状态',
      render: () => (
        <Select
          mode="multiple"
          allowClear
          placeholder="请选择流转状态"
          options={Object.entries(TransferStatusMap).map(([key, value]) => ({
            label: value,
            value: Number(key),
          }))}
        />
      ),
    },
  ]

  const columns = [
    {
      title: '提交时间',
      dataIndex: 'createTime',
      width: 180,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    {
      title: '原线索ID',
      dataIndex: 'origLeadId',
      width: 180,
    },
    {
      title: '批次ID',
      dataIndex: 'batchId',
      width: 180,
    },
    {
      title: '批次名称',
      dataIndex: 'batchName',
      width: 180,
    },
    {
      title: '线索标签',
      dataIndex: 'leadTags',
      width: 220,
      render: (tags) => (<Popover placement="topLeft" content={<div style={{ maxWidth: 400 }}>{tags?.join('，')}</div>}> <div className="text-truncate" style={{ cursor: 'pointer' }}>{tags?.join('，')}</div> </Popover>),
    },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      width: 280,
    },
    {
      title: '原客户名称',
      dataIndex: 'origLeadInfo.name',
      width: 280,
      render: (name, record) => {
        return (record.leadSource === 1 && record.origLeadInfo?.customerId !== 0) ? (
          <Link target="_blank" to={`/customer/${record.origLeadInfo?.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
            {trimStr(record?.origLeadInfo?.name, '-')}
          </Link>
        ) : (record?.origLeadInfo?.name || '-')
      },
    },
    {
      title: '客户所在省市',
      dataIndex: 'area',
      width: 180,
    },
    {
      title: '客户所属行业',
      dataIndex: 'firstIncomeIndustryName',
      width: 180,
      render: (text, record) => {
        return text || record.industry
      },
    },
    {
      title: '注册资本（人民币万元）',
      dataIndex: 'registeredCapital',
      width: 180,
    },
    {
      title: '联系人姓名',
      dataIndex: 'contactName',
      width: 180,
    },
    {
      title: '联系人电话',
      dataIndex: 'contactPhone',
      width: 180,
    },
    {
      title: '联系人职务',
      dataIndex: 'contactPosition',
      width: 180,
    },
    {
      title: '关注产品',
      dataIndex: 'needProduct',
      width: 180,
    },
    {
      title: '客户需求',
      dataIndex: 'businessDemand',
      width: 180,
    },
    {
      title: '销售通路',
      dataIndex: 'salesChannelName',
      width: 180,
    },
    {
      title: 'names商务',
      dataIndex: 'nameSales',
      width: 180,
    },
    {
      title: '线索来源',
      dataIndex: 'leadSourceName',
      width: 180,
    },
    {
      title: '跟进人',
      dataIndex: 'follower',
      width: 220,
      render: (follower) => <div className="text-truncate"><UserAvatar salesName={follower} /></div>,
    },
    {
      title: '转出人',
      dataIndex: 'transferSalesName',
      width: 220,
      render: (transferSalesName) => <div className="text-truncate"><UserAvatar salesName={transferSalesName} /></div>,
    },
    {
      title: '流转状态',
      dataIndex: 'status',
      width: 120,
      render: (status) => (
        <Tag color={TransferStatusColorMap[status]}>
          {TransferStatusMap[status]}
        </Tag>
      ),
    },
    {
      title: '操作',
      dataIndex: 'detail',
      width: 200,
      render: (text, record) => (
        <>
          <Popover content={
            <Descriptions column={2} size="small" style={{ width: '600px' }}>
              {text?.map?.(item => {
                return <Descriptions.Item key={item.keyName} label={item.keyName}>{item.keyValue === 'undefined' ? '-' : item.keyValue}</Descriptions.Item>
              })}
            </Descriptions>
          } title="详情">
            <Button type="link" size="small">详情</Button>
          </Popover>
          {authPointMap.mgtLeadListTransfer?.result && (
            <>
              <Button type="link" size="small" disabled={record.status !== TransferStatusKeyMap.ReadyToTransfer} onClick={() => handleEdit(record)}>
                编辑
              </Button>
              {
                (record.status === TransferStatusKeyMap.ReadyToTransfer) ? (
                  <Popconfirm title="确定流转吗？" onConfirm={() => handleSingleTransfer(record)}>
                    <Button type="link" size="small">流转</Button>
                  </Popconfirm>
                ) : (
                  <Button type="link" size="small" disabled>流转</Button>
                )
              }
              {
                (record.status === TransferStatusKeyMap.ReadyToTransfer) ? (
                  <Popconfirm title="确定打回吗？" onConfirm={() => handleSingleReject(record)}>
                    <Button type="link" size="small">打回</Button>
                  </Popconfirm>
                ) : (
                  <Button type="link" size="small" disabled>打回</Button>
                )
              }
            </>
          )}
        </>
      ),
    },
    {
      title: '流转通路',
      dataIndex: 'transferChannel',
      width: 180,
    },
    {
      title: '流转时间',
      dataIndex: 'transferTime',
      width: 180,
      sorter: true,
      sortOrder: filter.sort === 'transferTime' ? antdSortMap[filter.sortType] : undefined,
    },
  ]

  const [columnConfigs, columnProps, forceRequest] = useTableColumn(TableIdEnum.LEAD_TRANSFER, columns, [])
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])
  const onTableChange: TableProps<LeadTransferItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<LeadTransferListFilter>(sorter)
    setFilter(sort)
  }
  return (
    <>
      <PageHeader title="待转出意向线索" />
      <Card>
        <CommonFilters className="custom-Label" onReset={() => {
          resetFilter(initFilter)
        }} itemList={transferFilterItems} onSubmit={(filter) => {
          setFilter({ ...filter, page: 1 })
        }} />
      </Card>
      <Card>
        <ConfigTableHeader columnConfigs={columnConfigs} tableId={TableIdEnum.LEAD_TRANSFER} onSuccess={forceRequest} />
        <span className="ml20 mr15">{selectedRowText}</span>
        {authPointMap.mgtLeadListTransfer?.result
        && (
          <Popconfirm
            title="确定批量流转吗？"
            onConfirm={handleBatchTransfer}
            disabled={!selectedLeadTransferList.length}
            okButtonProps={{ disabled: !selectedLeadTransferList.length }}
          >
            <Button
              type="primary"
              className="ml20"
              onClick={(e) => {
                if (!selectedLeadTransferList.length) {
                  e.stopPropagation()
                  message.warning('请先勾选需要流转的线索')
                }
              }}
            >
              批量流转
            </Button>
          </Popconfirm>
        )}
        <TableDownload className="ml20" exportMethod={exportLeadTransferList} filter={filter} />
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={leadTransferList}
          columns={columnProps}
          onChange={onTableChange}
          rowSelection={rowSelection}
        />
      </Card>

      {/* 编辑意向线索模态框 */}
      {editModalOpen && (
        <IntentionLeadModal
          open={editModalOpen}
          mode="edit"
          editDetail={editingLeadDetail}
          onOk={handleEditModalOk}
          onCancel={handleEditModalCancel}
        />
      )}
      {/* 上传企微孵化线索编辑弹窗 */}
      {transferEditModalOpen && (
        <TransferLeadAddModal
          id={transferLeadId}
          open={transferEditModalOpen}
          onCancel={handleTransferEditModalCancel}
          onOk={handleTransferEditModalOk}
        />
      )}
    </>
  )
}
